import { peekDestination } from "@anthra/platform/session"
import { SwamithraLogo } from "@anthra/ui/brand-icons"
import { Button } from "@anthra/ui/button"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import { Skeleton, SkeletonText } from "@anthra/ui/skeleton"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ClockIcon } from "lucide-react"
import { useState } from "react"

import { useTheme } from "@/hooks/use-theme"

/**
 * Session lapsed (US-00-001.14/.15/.19/.20, US-00-002.21/.26, US-00-006.10/.21).
 *
 * Built against screens/session-lapsed.html: the signed-out header, the held
 * destination and its "what happens next" rail — a static column on desktop
 * (`.rail--desktop`), a `<details>` disclosure on mobile (`.rail--mobile`) so
 * the held destination is what fills the first screenful — and the hand off to
 * the entry screen's OTP step.
 *
 * A LINKABLE SCREEN, NOT THE 401 HANDLER ITSELF. `@/lib/unauthenticated` already
 * holds the destination and sends a lapsed session straight to /entry — that is
 * what US-00-001.14/.19 and US-00-002.21/.26 ask for, and it is covered by
 * `screens.test.tsx`. This route is what a held destination looks like on
 * screen, reachable on its own (a bookmarked link, a slow tab that only now
 * notices) without disturbing that already-verified path.
 *
 * THE HELD DESTINATION IS READ, NOT ASSUMED. `useSessionStore`'s in-memory copy
 * only exists for the tick between a 401 firing and the redirect it triggers —
 * a fresh load of this route (the bookmark, the slow tab) starts with an empty
 * store, so what this screen shows is `peekDestination()`'s answer from the
 * DURABLE store `@anthra/platform/session` holds it in, fetched once and shown
 * as a skeleton (screens/session-lapsed.html state 3) while it resolves. A
 * PEEK, not `takeDestination()`: this screen only shows what is held, and it
 * must still be there for the entry screen to consume exactly once after the
 * code is verified.
 *
 * WHAT THE DESIGN DRAWS THAT THIS DOES NOT: `.number-row`'s masked "code goes to
 * +91 ••••• •3210" and `.held`'s badge/"Expert replied…" line both need the
 * account the lapsed session belonged to — and a lapsed session is, by
 * definition, an identity this screen cannot ask the API for. Only the held
 * ROUTE survives, so the held item below names that path rather than a notice
 * title it has no way to know, and there is no second "use a different number"
 * control pointing at the same /entry destination as the primary button.
 */
export const Route = createFileRoute("/session-lapsed")({
	component: SessionLapsed,
})

function SessionLapsed() {
	const navigate = useNavigate()
	const heldDestination = useQuery({
		queryKey: ["session-lapsed", "held-destination"],
		queryFn: () => peekDestination(),
	})
	const [sending, setSending] = useState(false)
	const [theme, toggleTheme] = useTheme()

	function sendCode(): void {
		setSending(true)
		void navigate({ to: "/entry" })
	}

	if (heldDestination.isPending) return <SessionLapsedSkeleton />

	const destination = heldDestination.data ?? null

	const heldLabel = destination === null ? "Where you will land" : "We are holding this for you"
	const lede =
		destination === null
			? "Five days went by without activity, so we signed you out. Verify your WhatsApp number and you will be back on Home, with your queries and vetting requests exactly as you left them."
			: "We sign you out after 5 days without activity, so nobody who picks up your device can read your notices. Verify your WhatsApp number and we will take you straight back to where you were going."
	const steps = [
		"We send a 6-digit OTP to your WhatsApp, from the Swamithra business account.",
		"You enter it here. Three wrong OTPs lock the number for 30 minutes.",
		destination === null
			? "You land on Home, with everything where you left it."
			: "You land back on where you were going, not on Home. Nothing you wrote was lost.",
	]

	return (
		<div
			className="bg-canvas text-foreground flex min-h-dvh flex-col"
			data-screen="session-lapsed"
		>
			<SessionLapsedHeader
				theme={theme}
				toggleTheme={toggleTheme}
			/>

			{/* This container was nested inside an IDENTICAL copy of itself —
			    the same class string — so double gutters, double vertical
			    padding, and a two-column grid whose only child was another
			    two-column grid. */}
			<div className="mx-auto grid w-full max-w-[var(--width-page)] flex-1 gap-8 px-[var(--space-gutter-mobile)] py-8 md:px-[var(--space-gutter-tablet)] lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:px-[var(--space-gutter-desktop)]">
				<main className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4">
					<span
						className="bg-subtle text-primary rounded-circle flex size-12 items-center justify-center"
						aria-hidden="true"
					>
						<svg
							width="26"
							height="26"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M12 3.25 5 6v5.2c0 4.1 2.9 7.6 7 9.05 4.1-1.45 7-4.95 7-9.05V6Z" />
						</svg>
					</span>
					<h1 className="text-page-title text-foreground">Your session has ended</h1>
					<p className="text-body text-subtle-foreground max-w-prose">{lede}</p>

					<div className="rounded-card border-border bg-subtle flex flex-col gap-2 border p-4">
						<span className="text-overline text-muted uppercase">{heldLabel}</span>
						<div className="flex items-start gap-3">
							<svg
								className="text-primary size-5 shrink-0"
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<path d="M4 6.5h16v11H4z" />
								<path d="m4.5 7 7.5 5.5L19.5 7" />
							</svg>
							<div className="flex min-w-0 flex-col gap-0.5">
								<p className="text-card-title text-foreground">
									{destination ?? "Home"}
								</p>
								<p className="text-meta text-muted">
									<span>
										{destination === null
											? "You were not partway through anything when the session ended."
											: "Held for one sign-in"}
									</span>
								</p>
							</div>
						</div>
					</div>

					{/* ONE ACTION ROW, docked below `lg` and an ordinary row above it.

						    There were two "Send OTP" buttons: this one, and a copy
						    docked to the bottom of the viewport that went `lg:static`
						    rather than `lg:hidden` — so both were on screen at every
						    width, announced twice, and took two tab stops. Hiding one
						    per breakpoint would still leave two in the DOM; one
						    element that moves itself does the whole job. */}
					<div className="border-border bg-surface sticky bottom-0 flex flex-wrap gap-2 border-t p-4 lg:static lg:border-0 lg:bg-transparent lg:p-0">
						<button
							type="button"
							className={cn(buttonVariants({ variant: "primary" }))}
							disabled={sending}
							onClick={sendCode}
						>
							<svg
								width="18"
								height="18"
								viewBox="0 0 24 24"
								fill="currentColor"
								aria-hidden="true"
							>
								<path d="M12 2a10 10 0 0 0-8.6 15L2 22l5.2-1.4A10 10 0 1 0 12 2zm0 2a8 8 0 1 1-4.1 14.9l-.4-.2-2.6.7.7-2.5-.2-.4A8 8 0 0 1 12 4z" />
							</svg>
							{sending ? "Sending…" : "Send OTP"}
						</button>
						{destination !== null ? (
							<Link
								className={cn(buttonVariants({ variant: "ghost" }))}
								to="/home"
							>
								Go to Home instead
							</Link>
						) : null}
					</div>

					{/* State 2, sub-moment 2: WhatsApp dispatch can take a few seconds,
						    so this is a track that holds its position rather than a
						    spinner that implies an instant (screens/session-lapsed.html). */}
					{sending ? (
						<div
							className="bg-subtle rounded-pill h-1 w-full overflow-hidden"
							role="status"
							aria-label="Sending your OTP"
						>
							<div className="bg-primary rounded-pill h-full w-1/3 animate-pulse" />
						</div>
					) : null}

					<p className="text-meta text-muted">
						<ClockIcon
							className="size-5 shrink-0"
							aria-hidden="true"
						/>
						<span>
							The OTP is valid for 10 minutes. We will never ask you for it on a call
							or in a chat. Not even if the caller says they are from Swamithra.
						</span>
					</p>
				</main>

				<aside
					className="hidden flex-col gap-3 lg:flex"
					aria-label="What happens next"
				>
					<h2 className="text-section-title text-foreground">What happens next</h2>
					<ol className="flex flex-col gap-2">
						{steps.map((text, step) => (
							<li
								className="text-description text-subtle-foreground flex items-start gap-2"
								key={text}
							>
								{/* A SELF-CLOSING span: a styled circle with nothing in it,
									    so the list rendered as blank discs where 1, 2 and 3
									    belong. `aria-hidden` stays — the <ol> already numbers
									    itself for a screen reader. */}
								<span
									className="bg-subtle text-primary rounded-circle text-meta flex size-5 shrink-0 items-center justify-center font-semibold"
									aria-hidden="true"
								>
									{step + 1}
								</span>
								<span className="min-w-0">{text}</span>
							</li>
						))}
					</ol>
					<p className="text-meta text-muted">
						Your queries, notices and draft responses stayed encrypted while you were
						away. Signing out does not delete anything.
					</p>
				</aside>

				{/* The same rail, collapsed to a disclosure on mobile so the held
					    destination fills the first screenful. */}
				<details className="border-border rounded-card border p-3 lg:hidden">
					<summary>
						What happens next
						<svg
							width="18"
							height="18"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
						>
							<path d="M6 9l6 6 6-6" />
						</svg>
					</summary>
					<div className="mt-2 flex flex-col gap-2">
						<ol className="flex flex-col gap-2">
							{steps.map((text, step) => (
								<li
									className="text-description text-subtle-foreground flex items-start gap-2"
									key={text}
								>
									<span
										className="bg-subtle text-primary rounded-circle text-meta flex size-5 shrink-0 items-center justify-center font-semibold"
										aria-hidden="true"
									>
										{step + 1}
									</span>
									<span className="min-w-0">{text}</span>
								</li>
							))}
						</ol>
					</div>
				</details>
			</div>

			{/* Mobile: the single action pinned within thumb reach, the desktop
			    `.actions--desktop` pair hidden below screens/session-lapsed.css's
			    64rem breakpoint. */}
		</div>
	)
}

/** The signed-out header, identical on every state — shared so the skeleton below is not a second copy of it. */
function SessionLapsedHeader({
	theme,
	toggleTheme,
}: {
	theme: "light" | "dark"
	toggleTheme: () => void
}) {
	return (
		<header className="border-border bg-surface flex h-18 items-center gap-3 border-b px-[var(--space-gutter-mobile)] md:h-24 md:px-[var(--space-gutter-tablet)]">
			<SwamithraLogo
				src={`${import.meta.env.BASE_URL}images/swamithra-logo.png`}
				darkSrc={`${import.meta.env.BASE_URL}images/swamithra-logo-dark.png`}
				label="User Portal"
			/>
			<div className="ml-auto flex items-center gap-2">
				<span className="rounded-pill border-status-in-progress-border bg-status-in-progress-bg text-status-in-progress-fg text-meta hidden items-center gap-1.5 border px-2.5 py-1 sm:inline-flex">
					<span
						className="rounded-circle size-[var(--size-status-dot)] bg-current"
						aria-hidden="true"
					/>
					Signed out
				</span>
				<a
					className="text-description text-subtle-foreground hover:text-foreground ml-auto"
					href="/help"
				>
					<svg
						width="18"
						height="18"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<circle
							cx="12"
							cy="12"
							r="9"
						/>
						<path d="M9.5 9.5a2.5 2.5 0 113.4 2.3c-.6.3-.9.8-.9 1.4v.3" />
						<path d="M12 17h.01" />
					</svg>
					<span className="text-description text-subtle-foreground hover:text-foreground">
						Help
					</span>
				</a>
				<Button
					type="button"
					variant="quiet"
					size="icon"
					onClick={toggleTheme}
					aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
				>
					<svg
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<path d="M20 14.4A8.4 8.4 0 0 1 9.6 4 8.4 8.4 0 1 0 20 14.4Z" />
					</svg>
					{/* The icon alone. The label was rendered as visible text INSIDE a
					    fixed 44x44 square, so "Dark theme" was squeezed into 48px and
					    wrapped — and it duplicated the aria-label the button already
					    carries. */}
				</Button>
			</div>
		</header>
	)
}

/**
 * Loading (screens/session-lapsed.html state 3): resolving the held
 * destination from the durable store before the card and rail can say what is
 * held. Skeletons take the shape of what follows — seal, title, lede, the held
 * panel, the masked number row, the actions and the steps rail — so nothing
 * jumps once `peekDestination()` answers.
 */
function SessionLapsedSkeleton() {
	const [theme, toggleTheme] = useTheme()

	return (
		<div
			className="bg-canvas text-foreground flex min-h-dvh flex-col"
			data-screen="session-lapsed"
			aria-busy="true"
		>
			<SessionLapsedHeader
				theme={theme}
				toggleTheme={toggleTheme}
			/>

			<div className="mx-auto grid w-full max-w-[var(--width-page)] flex-1 gap-8 px-[var(--space-gutter-mobile)] py-8 md:px-[var(--space-gutter-tablet)] lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:px-[var(--space-gutter-desktop)]">
				<main className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4">
					<p
						role="status"
						className="sr-only"
					>
						Checking your session and where you were going.
					</p>
					<SkeletonText lines={3} />

					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-full" />
				</main>

				<aside
					className="hidden flex-col gap-3 lg:flex"
					aria-hidden="true"
				>
					<SkeletonText lines={3} />
				</aside>
			</div>

			<div className="border-border bg-surface sticky bottom-0 flex flex-col gap-2 border-t p-4 lg:static lg:border-0 lg:bg-transparent lg:p-0">
				<Skeleton className="h-4 w-full" />
			</div>
		</div>
	)
}
